"use client";
import { JSX, Fragment } from "react";
import { useTranslations } from "next-intl";
import { Hand, Smile } from "lucide-react";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";

/** Props for the Reactions component */
interface ReactionsProps {
  isHandRaised: boolean;
  onToggleHandRaise: () => void;
  onSendReaction: (emoji: string) => void;
}

const EMOJI_REACTIONS = ["👍", "👏", "❤️", "😂", "🎉", "🔥", "🤔", "👎"];

/** Reactions - Controls for raising hand and sending emojis */
export const Reactions = ({ isHandRaised, onToggleHandRaise, onSendReaction }: ReactionsProps): JSX.Element => {
  const t = useTranslations();
  const toolbarFocusRing =
    "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-white";
  const reactionButtonBase = `cursor-pointer border p-3 sm:p-3.5 rounded-full transition-all duration-200 active:scale-[0.97] ${toolbarFocusRing}`;

  return (
    <div className="flex items-center gap-3">
      {/* Raise Hand */}
      <button
        onClick={onToggleHandRaise}
        className={`relative ${reactionButtonBase} ${
          isHandRaised
            ? "border-amber-400 bg-amber-400 text-slate-950 shadow-lg shadow-amber-400/40 hover:border-amber-500 hover:bg-amber-300 hover:ring-2 hover:ring-amber-400/30 ring-2 ring-amber-300 ring-offset-2 ring-offset-white"
            : "border-gray-200/70 bg-white text-gray-600 shadow-md hover:border-primary hover:bg-gray-50 hover:shadow-lg hover:ring-2 hover:ring-primary/15 hover:-translate-y-0.5"
        }`}
        aria-label={isHandRaised ? t("controls.lowerHand") : t("controls.raiseHand")}
        aria-pressed={isHandRaised}
        title={isHandRaised ? `${t("controls.lowerHand")} (Ctrl+Alt+H)` : `${t("controls.raiseHand")} (Ctrl+Alt+H)`}
      >
        <Hand className={`w-5 h-5 transition-transform duration-300 ${isHandRaised ? "scale-110" : ""}`} />
      </button>

      {/* Emoji Reactions Menu */}
      <Menu as="div" className="relative group">
        <MenuButton
          className={`group ${reactionButtonBase} border-gray-200/70 bg-white text-gray-600 shadow-md hover:border-primary hover:bg-gray-50 hover:shadow-lg hover:ring-2 hover:ring-primary/15 hover:-translate-y-0.5`}
          aria-label={t("controls.sendReaction")}
        >
          <Smile className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
        </MenuButton>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-200 cubic-bezier(0.16, 1, 0.3, 1)"
          enterFrom="transform opacity-0 translate-y-2 scale-90"
          enterTo="transform opacity-100 translate-y-0 scale-100"
          leave="transition ease-in duration-150"
          leaveFrom="transform opacity-100 translate-y-0 scale-100"
          leaveTo="transform opacity-0 translate-y-2 scale-90"
        >
          <MenuItems className="absolute bottom-full mb-4 right-0 origin-bottom-right bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl ring-1 ring-black/5 p-2 grid grid-cols-8 gap-1 focus:outline-none min-w-[400px] z-50">
            {EMOJI_REACTIONS.map((emoji) => (
              <MenuItem key={emoji}>
                {({ focus }) => (
                  <button
                    onClick={() => onSendReaction(emoji)}
                    className={`flex cursor-pointer items-center justify-center rounded-xl border border-transparent p-0 text-2xl transition-all duration-200 ease-out hover:scale-110 hover:border-gray-200 active:scale-90 ${
                      focus ? "border-gray-200 bg-gray-100 shadow-inner" : "hover:bg-gray-100"
                    }`}
                    aria-label={t("controls.sendEmoji", { emoji })}
                  >
                    <span className="transform transition-transform hover:rotate-6">{emoji}</span>
                  </button>
                )}
              </MenuItem>
            ))}
          </MenuItems>
        </Transition>
      </Menu>
    </div>
  );
};
